import React, { useCallback, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import LoadFailure from "../../components/ui/LoadFailure";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import useAdminLoad from "../../hooks/useAdminLoad";
import { getPathway } from "../../pathways/adminApi";
import { toPreviewHome, toPreviewStepScreen } from "../../pathways/previewModel";
// The learner lane's presentational views (contract §9.1): the preview IS the learner UI.
import PathwayHomeView from "../../public/pathways/PathwayHomeView";
import StepScreenView from "../../public/pathways/StepScreenView";
import { adminHref } from "../adminPath";
import { pathwayPaths } from "./paths";

/** The preview never records anything: completion and quiz submission are refused. */
const refused = () => Promise.reject(new Error("preview"));

/**
 * `/admin/pathways/:pathwayId/preview[?step=<stepId>]`: "Preview as learner" (spec screen
 * 2, contract §9.1). The STORED pathway is rendered through the learner's own home and
 * step screens, with everything unlocked and completion disabled (`preview`), so what
 * the practitioner inspects is what a learner will see.
 *
 * The open step is in the address (`?step=`), so the browser's back button walks back
 * from a step to the home, as a learner's does.
 */
export default function PathwayPreview() {
  const t = useT("admin");
  const tc = useT("common");
  const { pathwayId } = useParams();
  const [params, setParams] = useSearchParams();
  const [pathway, setPathway] = useState(null);
  const load = useCallback(async () => { setPathway(await getPathway(pathwayId)); }, [pathwayId]);
  const { loading, loadError, reload } = useAdminLoad(load);
  const stepId = params.get("step");

  const home = useMemo(() => (pathway ? toPreviewHome(pathway) : null), [pathway]);
  const screen = useMemo(() => (pathway && stepId ? toPreviewStepScreen(pathway, stepId) : null), [pathway, stepId]);
  const openStep = (id) => setParams(id ? { step: id } : {});

  return (
    <div data-testid={IDS.preview} className="space-y-4">
      <div
        data-testid={IDS.previewBanner}
        role="status"
        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-eh-border-soft bg-eh-cream/70 px-4 py-3 text-sm text-eh-ink"
      >
        <span className="inline-flex items-center gap-2">
          <Eye className="w-4 h-4" aria-hidden="true" /> {t("path.preview.banner")}
        </span>
        <Link to={adminHref(pathwayPaths.edit(pathwayId, "structure"))} data-testid={IDS.previewBack} className="inline-flex items-center gap-1.5 font-semibold underline">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> {t("path.preview.back")}
        </Link>
      </div>

      {loading && !pathway ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-eh-muted" aria-label={t("path.loading")} /></div>
      ) : loadError || !home ? (
        <LoadFailure message={tc("errors.loadFailed")} onRetry={reload} retryLabel={t("path.retry")} />
      ) : screen ? (
        <StepScreenView
          screen={screen}
          onBack={() => openStep(null)}
          onNext={(id) => openStep(id)}
          onComplete={refused}
          onSubmitQuiz={refused}
          preview
        />
      ) : (
        <PathwayHomeView home={home} onOpenStep={(id) => openStep(id)} preview />
      )}
    </div>
  );
}
